import {
  Box,
  Button,
  Dialog,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  GamificationContext,
  LockedModalClickFrom,
} from "contexts/Gamification";
import { OrganizationUserContext } from "contexts/Organization";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import { trackEvent } from "utils/tracking";

import { OrganizationGamificationTask } from "../schema";
import styles from "./styles";

const ONBOARDING_STEPS = [
  {
    id: "add_portfolio",
    label: "Add Your Portfolio Link",
    taskName: OrganizationGamificationTask.add_portfolio,
  },
  {
    id: "add_template",
    label: "Create Your Pitch Template",
    taskName: OrganizationGamificationTask.add_template,
  },
  {
    id: "add_followup",
    label: "Create Your Follow-Up Template",
    taskName: OrganizationGamificationTask.add_followup,
  },
  {
    id: "send_pitch",
    label: "Send 10 Brand Pitches",
    taskName: OrganizationGamificationTask.send_pitch,
  },
];

interface UnlockDirectDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const UnlockDirectDialog = ({ isOpen, onClose }: UnlockDirectDialogProps) => {
  const { gamificationProfile } = useContext(OrganizationUserContext);
  const { openLockedModal } = useContext(GamificationContext);
  const navigate = useNavigate();

  const completedSteps = ONBOARDING_STEPS.filter((step) =>
    gamificationProfile?.tasksCompleted?.includes(step.taskName),
  ).length;

  const { closeDrawer } = useContext(QuickSendDrawerContext);

  const handleCompleteSteps = () => {
    onClose();
    trackEvent("Gamification - Click Complete Steps On Unlock Direct Contacts");
    navigate(`/${routes.home}`);
    if (openLockedModal === LockedModalClickFrom.email) closeDrawer();
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent("Gamification - Open Unlock Direct Contacts Dialog");
    }
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{ sx: styles.dialogPaper }}
    >
      {/* Header */}
      <Box sx={styles.header}>
        <IconButton onClick={onClose} sx={styles.closeButton} size="small">
          <Box
            component="i"
            className="fa-solid fa-times"
            sx={{ fontSize: 16 }}
          />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={styles.lockIconCircle}>
            <Box
              component="i"
              className="fa-regular fa-lock"
              sx={styles.lockIcon}
            />
          </Box>
          <Box>
            <Typography
              fontWeight={700}
              fontSize="1.1rem"
              sx={{ color: "white" }}
            >
              {openLockedModal === LockedModalClickFrom.email
                ? "Unlock Direct Contacts"
                : "This feature is not available until you complete onboarding"}
            </Typography>
            <Typography sx={styles.headerSubtitle}>Premium Feature</Typography>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={styles.content}>
        <Typography sx={styles.descriptionText}>
          {openLockedModal === LockedModalClickFrom.email ? (
            <>
              <strong>This contact has a direct email address.</strong> Complete
              all onboarding steps to unlock direct brand contacts and upgrade
              from 10K to 500K+ brands!
            </>
          ) : (
            "Complete the onboarding steps below to unlock all features and access 500K+ brands!"
          )}
        </Typography>

        {/* Progress */}
        <Box sx={styles.progressBox}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={styles.progressLabel}>Your Progress</Typography>
            <Typography sx={styles.progressCount}>
              {completedSteps} of 4 steps
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(completedSteps / 4) * 100}
            sx={styles.progressBar}
          />
        </Box>

        {/* Steps list */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2.5 }}>
          {ONBOARDING_STEPS.map((step) => {
            const completed =
              gamificationProfile?.tasksCompleted?.includes(step.taskName) ??
              false;
            return (
              <Box key={step.id} sx={styles.stepItem(completed)}>
                <Box
                  component="i"
                  className={
                    completed
                      ? "fa-solid fa-circle-check"
                      : "fa-regular fa-circle"
                  }
                  sx={styles.stepIcon(completed)}
                />
                <Typography sx={styles.stepLabel(completed)}>
                  {step.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Benefits */}
        <Box sx={styles.benefitsBox}>
          <Box
            component="i"
            className="fa-solid fa-crown"
            sx={styles.crownIcon}
          />
          <Box>
            <Typography sx={styles.benefitTitle}>
              What You'll Unlock:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {[
                "Direct brand partnership emails",
                "Access to 500K+ brands",
                "Advanced features",
              ].map((benefit) => (
                <Box key={benefit} sx={styles.benefitItem}>
                  <Box
                    component="i"
                    className="fa-solid fa-sparkles"
                    sx={styles.sparkleIcon}
                  />
                  <Typography fontSize="0.8rem" color="#374151">
                    {benefit}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={styles.actions}>
          <Button variant="outlined" onClick={onClose} sx={styles.closeBtn}>
            Close
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleCompleteSteps}
            sx={styles.completeBtn}
          >
            Complete
            <Box
              component="i"
              className="fa-solid fa-arrow-right"
              sx={styles.arrowIcon}
            />
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default UnlockDirectDialog;
